import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThumbsUpIcon, ThumbsDownIcon } from './icons';
import { HelpArticleFull, HelpArticleMeta } from './helpdeskApi';

// Ported verbatim from templates.js INTENT_CTA — the CTA label/href shown at
// the bottom of every article, chosen by the article's `intent` field.
const INTENT_CTA: Record<string, { label: string; href: string }> = {
    setup: { label: 'Open your dashboard', href: 'https://traffic-creator.com/dashboard' },
    troubleshooting: {
        label: 'Contact support with your project ID',
        href: '/helpdesk/support/contact-trafficcreator-support',
    },
    transactional: { label: 'Open billing history', href: 'https://traffic-creator.com/dashboard' },
    reference: { label: 'Open your dashboard', href: 'https://traffic-creator.com/dashboard' },
    policy: {
        label: 'Contact support before running this use case',
        href: '/helpdesk/support/contact-trafficcreator-support',
    },
};

const formatLastReviewed = (iso?: string | null): string => {
    if (!iso) return '';
    const parsed = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return iso;
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

interface ArticleViewProps {
    article: HelpArticleFull;
    categoryTitle: string;
    relatedArticles: HelpArticleMeta[];
}

/**
 * Full article page: quick-answer box, rendered prose body, scroll-spy TOC,
 * "Was this helpful?" feedback, related articles, and the intent-based CTA.
 * Ported from `articlePage()` in prototype/lib/templates.js. `article.content`
 * is pre-rendered, sanitized HTML from the backend (same trust model as
 * Blog.tsx's `activeArticleContent`), so it is rendered the same way here.
 */
const ArticleView: React.FC<ArticleViewProps> = ({ article, categoryTitle, relatedArticles }) => {
    const [feedbackGiven, setFeedbackGiven] = useState(false);
    const [activeTocId, setActiveTocId] = useState<string | null>(null);

    const cta = (article.intent && INTENT_CTA[article.intent]) || INTENT_CTA.reference;
    const isExternalCta = /^https?:/.test(cta.href);
    const showToc = article.toc.length >= 3;

    // TOC scroll-spy, re-implemented from the IntersectionObserver block in
    // prototype/public/client.js. Runs after the sanitized HTML is in the DOM
    // so the heading ids exist to observe.
    useEffect(() => {
        if (!showToc || !('IntersectionObserver' in window)) return;
        const headingElements = article.toc
            .map((entry) => document.getElementById(entry.id))
            .filter((el): el is HTMLElement => el !== null);
        if (headingElements.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveTocId(entry.target.id);
                    }
                });
            },
            { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
        );
        headingElements.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [article.slug, showToc, article.toc]);

    const handleFeedback = (_vote: 'yes' | 'no') => {
        // Prototype parity note: no backend write yet. The real version would
        // log { vote, articleSlug, timestamp } — see deviations in the final report.
        setFeedbackGiven(true);
    };

    return (
        <article className="hc-article">
            <div className="hc-wrap hc-article__grid">
                <div className="hc-article__main">
                    <div className="hc-article__meta">
                        {article.intent && <span className={`hc-pill hc-pill--${article.intent}`}>{article.intent}</span>}
                        {article.lastReviewed && (
                            <span className="hc-article__date">Last reviewed {formatLastReviewed(article.lastReviewed)}</span>
                        )}
                    </div>
                    <h1 className="hc-article__title">{article.title}</h1>

                    {article.summary && (
                        <div className="hc-answer">
                            <span className="hc-answer__label">Quick answer</span>
                            <p>{article.summary}</p>
                        </div>
                    )}

                    <div
                        className="hc-prose"
                        // Content is pre-rendered, sanitized HTML from the backend
                        // (backend/routers/help.py `content` field) — same trust
                        // model as Blog.tsx's activeArticleContent rendering.
                        dangerouslySetInnerHTML={{ __html: article.content }}
                    />

                    <div className="hc-feedback">
                        <p className="hc-feedback__q">Was this article helpful?</p>
                        {!feedbackGiven ? (
                            <div className="hc-feedback__btns">
                                <button
                                    type="button"
                                    className="hc-feedback__btn hc-feedback__btn--yes"
                                    onClick={() => handleFeedback('yes')}
                                >
                                    <ThumbsUpIcon /> Yes
                                </button>
                                <button
                                    type="button"
                                    className="hc-feedback__btn hc-feedback__btn--no"
                                    onClick={() => handleFeedback('no')}
                                >
                                    <ThumbsDownIcon /> No
                                </button>
                            </div>
                        ) : (
                            <p className="hc-feedback__thanks">Thanks for the feedback.</p>
                        )}
                    </div>

                    <div className="hc-article__cta">
                        <div>
                            <p className="hc-article__cta-title">Still need help?</p>
                            <p className="hc-article__cta-sub">If this didn&rsquo;t solve it, we&rsquo;re one message away.</p>
                        </div>
                        {isExternalCta ? (
                            <a className="hc-btn hc-btn--primary" href={cta.href} target="_blank" rel="noopener noreferrer">
                                {cta.label}
                            </a>
                        ) : (
                            <Link className="hc-btn hc-btn--primary" to={cta.href}>
                                {cta.label}
                            </Link>
                        )}
                    </div>
                </div>

                <div className="hc-article__side">
                    {showToc && (
                        <aside className="hc-toc" aria-label="On this page">
                            <p className="hc-toc__label">On this page</p>
                            <ol>
                                {article.toc.map((entry) => (
                                    <li key={entry.id} className={`hc-toc__l${entry.level}`}>
                                        <a
                                            href={`#${entry.id}`}
                                            className={activeTocId === entry.id ? 'is-active' : undefined}
                                        >
                                            {entry.text}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </aside>
                    )}
                    {relatedArticles.length > 0 && (
                        <div className="hc-rel">
                            <p className="hc-rel__label">Related articles</p>
                            {relatedArticles.map((related) => (
                                <Link key={related.url} className="hc-rel-card" to={related.url}>
                                    <span className="hc-rel-card__cat">{categoryTitle}</span>
                                    <span className="hc-rel-card__title">{related.title}</span>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
};

export default ArticleView;
